import { useMemo } from 'react';
import { Survey } from '../interfaces/survey.interface';
import { Questionnaire } from '../interfaces/questionnaire.interface';
import { QuestionStatusById } from '../app/store/questionnaireAnswerSlice';
import { QuestionAnswer } from '../interfaces/questionAnswer.interface';
import { Question } from '../interfaces/question.interface';
import { FULLY_IMPLEMENTED, NOT_APPLICABLE, NOT_IMPLEMENTED } from '../services/surveyHelpers';
import { QuestionnaireTypes } from '../services/questionnaires';
import { WorkflowTypeKey } from '../interfaces/workflow.interface';
import { TPN_BEST_PRACTICE_5_1, TPN_BEST_PRACTICE_5_2 } from '../services/workflow';
import { Domain } from '../interfaces/domain.interface';
import { BestPractice } from '../interfaces/bestPractice.interface';
import { Topic } from '../interfaces/topic.interface';

interface Params {
  baseline?: Survey;
  questionStatus?: QuestionStatusById;
  questionnaire?: Questionnaire;
  isQuestionniareBestPractice: boolean;
  workflowType?: WorkflowTypeKey;
}

type AnswerId = string | number;

const EXCLUDED_LABELS = new Set([
  NOT_APPLICABLE,
  FULLY_IMPLEMENTED,
  NOT_IMPLEMENTED,
]);

type QA = {
  answer?: AnswerId[];
  compensatingControlAnswers?: AnswerId[];
  nonApplicableAnswers?: AnswerId[];
  nonImplementedAnswers?: AnswerId[];
};

const isRequiredAnswerMissing = (
  entry: { question: Question; status: string; questionAnswer?: QA },
  allowedSlugs: Set<string>,
  isWfPreFiveThree?: boolean,
): boolean => {
  const { question, status, questionAnswer } = entry;
  const slug = (question.questionnaire as Questionnaire)?.slug;
  if (!allowedSlugs.has(slug)) return false;

  if (question.type === 'single_select') {
    return status === 'incomplete' || !(questionAnswer?.answer?.length);
  }

  const requiredAnswers = (question.answers ?? []).filter(
    (a) => !EXCLUDED_LABELS.has(a.text || ''),
  );
  if (requiredAnswers.length === 0) return false;

  const selectedIds = new Set<AnswerId>([
    ...(questionAnswer?.answer ?? []),
    ...(questionAnswer?.compensatingControlAnswers ?? []),
    ...(questionAnswer?.nonApplicableAnswers ?? []),
    ...(questionAnswer?.nonImplementedAnswers ?? []),
  ]);

  // If pre-5.3, any answer is acceptable
  return isWfPreFiveThree
    ? (questionAnswer?.answer ?? []).length === 0
    : requiredAnswers.some((a) => !selectedIds.has(a.id as AnswerId));
};

export function useQuestionnaireComplete({
  baseline,
  questionStatus,
  questionnaire,
  isQuestionniareBestPractice,
  workflowType,
}: Params): {
    baselineCompleted: boolean;
    questionnaireCompleted: boolean;
    incompleteBestPractices: BestPractice[];
    incompleteQuestionIds: { [key: number]: number };
  } {
  const invalidWorkflowType: boolean = typeof workflowType !== 'string';

  const baselineCompleted = useMemo(() => {
    if (!baseline?.questionAnswers || invalidWorkflowType) return false;

    if (isQuestionniareBestPractice) {
      return !(baseline.questionAnswers as QuestionAnswer[]).some(
        (qa) => typeof qa !== 'number' && !qa?.answer?.length,
      );
    }

    return Object.values(questionStatus || {}).every(({ questionAnswer, status }) => {
      if (!questionAnswer || invalidWorkflowType) return false;
      return status !== 'incomplete' && Array.isArray(questionAnswer.answer) && questionAnswer.answer.length > 0;
    });
  }, [baseline?.questionAnswers, questionStatus, isQuestionniareBestPractice, invalidWorkflowType]);

  const { questionnaireCompleted, incompleteBestPractices, incompleteQuestionIds = {} } = useMemo(() => {
    if (!questionStatus || !questionnaire?.slug || !(baseline?.questionnaire as Questionnaire)?.slug) {
      return { questionnaireCompleted: false, incompleteBestPractices: [] };
    }

    const questionnaireSlug = questionnaire.slug;
    const baselineSlug = (baseline?.questionnaire as Questionnaire).slug;
    const allowedSlugs = new Set([questionnaireSlug, baselineSlug]);

    const localIncompleteQuestionIds: { [key: number]: number } = {};
    const localIncomplete: BestPractice[] = [];
    for (const value of Object.values(questionStatus)) {
      const qType = (value.question.questionnaire as Questionnaire)?.type || '';
      if (
        value.status === 'hide' ||
        [QuestionnaireTypes.APPLICATION_BASELINE, QuestionnaireTypes.SITE_BASELINE].includes(qType)
      ) continue;

      const isTpnbp =
        typeof workflowType === 'string' &&
        [TPN_BEST_PRACTICE_5_1, TPN_BEST_PRACTICE_5_2].includes(workflowType);

      const hasMissing = isRequiredAnswerMissing(value, allowedSlugs, isTpnbp);
      if (hasMissing && isQuestionniareBestPractice) {
        localIncompleteQuestionIds[value.question.id as number] = value.question.id as number;
        const bp =
          questionnaire.domains
            .find((d) => d.id === (value.question.domain as Domain).id)
            ?.topics.find((t) => t.id === (value.question.topic as Topic).id)
            ?.bestPractices.find((b) => b.id === (value.question.bestPractice as BestPractice).id);

        if (bp) {
          const resolvedDomain =
              typeof bp.domain === 'number'
                ? questionnaire.domains.find((d) => d.id === bp.domain)
                : bp.domain;

          const resolvedTopic =
              typeof bp.topic === 'number'
                ? resolvedDomain?.topics.find((t) => t.id === bp.topic)
                : bp.topic;

          const resolvedQuestionnaire =
              typeof bp.questionnaire === 'number'
                ? questionnaire
                : bp.questionnaire;

          localIncomplete.push({
            ...bp,
            domain: resolvedDomain as Domain,
            topic: resolvedTopic as Topic,
            questionnaire: resolvedQuestionnaire,
          });
        }
      }
    }

    return {
      questionnaireCompleted: localIncomplete.length === 0,
      incompleteBestPractices: localIncomplete,
      incompleteQuestionIds: localIncompleteQuestionIds,
    };
  }, [questionStatus, questionnaire, baseline?.questionnaire, workflowType, isQuestionniareBestPractice]);

  return {
    baselineCompleted,
    questionnaireCompleted,
    incompleteBestPractices,
    incompleteQuestionIds,
  };
}
